import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  InputLabel,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";

const paymentOptions = ["Efectivo", "Transferencia", "Tarjeta Débito", "Tarjeta Crédito", "QR", "Otro"];

export default function PaymentMethodsModal({ 
  open, 
  onClose, 
  onSave,
  initialMethods = [], 
  initialExtra = 0, 
  subtotal 
}) {
  const [methods, setMethods] = useState([...initialMethods]);
  const [amountMismatchError, setAmountMismatchError] = useState(false);
  
  const [extraValue, setExtraValue] = useState(0);
  const [isBonusExtra, setIsBonusExtra] = useState(false);
  const [showExtra, setShowExtra] = useState(false);

  const totalPayments = methods.reduce((sum, m) => sum + m.amount, 0);

  const handleAddMethod = () => {
    setAmountMismatchError(false);

    setMethods((prev) => {
      // Si es el primer metodo, darle el valor de subtotal
      if (prev.length === 0) {
        return [
          { 
            method: "", 
            amount: subtotal, 
            adjustment: 0, 
            isBonus: false,
          },
        ];  
      }

      // Si ya hay uno, resetear todo a 0
      const resetMethods = prev.map((m) => ({ ...m, amount: "" }));
      return [
        ...resetMethods,
        { method: "", amount: "", adjustment: 0, isBonus: false },
      ];
    });
  };

  const handleChange = (index, field, value) => {
    setAmountMismatchError(false);
    setMethods((prev) => {
      const newMethods = [...prev];
      if (field === "amount" || field === "adjustment") {
        const parsedValue = value === "" ? "" : parseFloat(value) || 0;
        newMethods[index][field] = parsedValue;
      } else {
        newMethods[index][field] = value;
      }
      return newMethods;
    });
  };

  const toggleAdjustment = (index, isBonus) => {
    setMethods((prev) => {
      const updated = [...prev];
      let adjustment = Math.abs(updated[index].adjustment);
      updated[index].adjustment = isBonus ? -adjustment : adjustment;
      updated[index].isBonus = isBonus;
      return updated;
    });
  };

  const handleDelete = (index) => {
    setAmountMismatchError(false);
    setMethods((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setMethods([]);
    setAmountMismatchError(false);
    setShowExtra(false);
    setExtraValue(0);
    setIsBonusExtra(false);
  };

  const handleSave = () => {
    const adjustedExtra = isBonusExtra ? Math.abs(extraValue) : -Math.abs(extraValue);

    if (Math.abs(totalPayments - subtotal) > 0.01) {
      setAmountMismatchError(true);
      return;
    }

    const cleaned = methods.map(({ isBonus, adjustment, ...r }) => ({
      ...r,
      adjustment: isBonus 
        ? -Math.abs(adjustment || 0) 
        : Math.abs(adjustment || 0),
    }));

    onSave(cleaned, adjustedExtra);
    onClose();
  };

  useEffect(() => {
    if (open && initialMethods.length === 0 && methods.length === 0) {
      setMethods([
        {
          method: "",
          amount: subtotal,
          adjustment: 0,
          isBonus: false,
        },
      ]);
    }
  }, [open, initialMethods, methods.length, subtotal]);

  useEffect(() => {
    if (initialExtra !== 0) {
      setExtraValue(Math.abs(initialExtra));
      setIsBonusExtra(initialExtra > 0);
      setShowExtra(true);
    } else {
      setExtraValue(0);
      setIsBonusExtra(false);
      setShowExtra(false);
    }
  }, [initialExtra]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Seleccionar Métodos de Pago - Subtotal: ${subtotal}
      </DialogTitle>
      <DialogContent>
        {methods.map((method, index) => (
          <Box
            key={index}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              py: 1,
              flexWrap: "wrap",
            }}
          >
            <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
              <InputLabel>Método</InputLabel>
              <Select
                value={method.method}
                label="Método"
                onChange={(e) => handleChange(index, "method", e.target.value)}
              >
                {paymentOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Monto"
              type="number"
              size="small"
              value={method.amount}
              onChange={(e) => handleChange(index, "amount", e.target.value)}
              sx={{ flex: 1, minWidth: 120 }}
            />

            <TextField
              label={method.isBonus ? "Bonificación" : "Recargo"}
              type="number"
              size="small"
              value={
                method.adjustment === 0 && method.adjustment !== ""
                  ? ""
                  : Math.abs(method.adjustment)
              }
              onChange={(e) => handleChange(index, "adjustment", e.target.value)}
              sx={{ flex: 1, width: 120 }}
            />

            <Button
              variant={method.isBonus ? "outlined" : "contained"}
              size="small"
              color="warning"
              onClick={() => toggleAdjustment(index, false)}
            >
              Recargo
            </Button>
            <Button
              variant={method.isBonus ? "contained" : "outlined"}
              size="small"
              color="success"
              onClick={() => toggleAdjustment(index, true)}
            >
              Bonificación
            </Button>

            <IconButton onClick={() => handleDelete(index)} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        {methods.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No se han añadido métodos de pago.
          </Typography>
        )}
      </DialogContent>

      <Box sx={{ px: 3, mt: 1 }}>
        <Typography variant="subtitle2">
          Total de métodos de pago: ${totalPayments}
        </Typography>
      </Box>

      {amountMismatchError && (
        <Typography color="error" sx={{ px: 3, pb: 1 }}>
          El monto total de los métodos de pago debe ser igual al subtotal.
        </Typography>
      )}

      <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={handleAddMethod}>Añadir Método</Button>
          {!showExtra ? (
            <Button variant="outlined" onClick={() => setShowExtra(true)}>Añadir Extra</Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                label={isBonusExtra ? "Bonificación Extra" : "Recargo Extra"}
                type="number" size="small" value={Math.abs(extraValue)}
                onChange={e => setExtraValue(parseFloat(e.target.value) || 0)}
                sx={{ width: 150 }}
              />
              <Button variant={!isBonusExtra ? "contained" : "outlined"} color="warning" size="small" onClick={() => setIsBonusExtra(false)}>Recargo</Button>
              <Button variant={isBonusExtra ? "contained" : "outlined"} color="success" size="small" onClick={() => setIsBonusExtra(true)}>Bonificación</Button>
              <IconButton color="error" onClick={() => { setShowExtra(false); setExtraValue(0); setIsBonusExtra(false); }}><DeleteIcon /></IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={handleReset} color="secondary">Reestablecer</Button>
          <Button onClick={onClose}>Cerrar</Button>
          <Button variant="contained" color="primary" onClick={handleSave}>Guardar</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
